// Enhanced Translation Helper Functions with Level System & Admin Panel Support
// File: client/src/utils/translationHelpers.js

import { getTranslation, isRTL } from '../translations/translations';

/**
 * Advanced translation helper with string interpolation
 * @param {string} key - Translation key
 * @param {string} language - Current language
 * @param {Object} params - Parameters for string interpolation
 * @param {string} fallback - Fallback text if translation not found
 */
export const t = (key, language = 'en', params = {}, fallback = null) => {
  let translation = getTranslation(key, language, fallback);
  
  // Handle string interpolation using simple string replacement
  if (params && Object.keys(params).length > 0) {
    Object.keys(params).forEach(param => {
      const placeholder = `{${param}}`;
      // Convert parameter value to string and use split/join for safe replacement
      const value = String(params[param] ?? '');
      translation = translation.split(placeholder).join(value);
    });
  }
  
  return translation;
};

/**
 * Pluralization helper for translations
 * @param {string} key - Base translation key
 * @param {number} count - Count for pluralization
 * @param {string} language - Current language
 */
export const tPlural = (key, count, language = 'en') => {
  const isHebrew = language === 'he';
  
  if (count === 0) {
    return t(`${key}.zero`, language) || t(key, language);
  } else if (count === 1) {
    return t(`${key}.one`, language) || t(key, language);
  } else if (count === 2 && isHebrew) {
    // Hebrew has dual form
    return t(`${key}.two`, language) || t(`${key}.many`, language) || t(key, language);
  } else {
    return t(`${key}.many`, language) || t(key, language);
  }
};

/**
 * Format numbers according to language conventions
 * @param {number} number - Number to format
 * @param {string} language - Current language
 */
export const formatNumber = (number, language = 'en') => {
  const locale = language === 'he' ? 'he-IL' : 'en-US';
  return new Intl.NumberFormat(locale).format(number);
};

/**
 * Format currency according to language conventions
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 * @param {string} language - Current language
 */
export const formatCurrency = (amount, currency = 'USD', language = 'en') => {
  const locale = language === 'he' ? 'he-IL' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(amount);
};

/**
 * Format dates according to language conventions
 * @param {Date|string} date - Date to format
 * @param {string} language - Current language
 * @param {Object} options - Intl.DateTimeFormat options
 */
export const formatDate = (date, language = 'en', options = {}) => {
  const locale = language === 'he' ? 'he-IL' : 'en-US';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
};

/**
 * Format relative time (e.g., "5 minutes ago")
 * @param {Date|string} date - Date to format
 * @param {string} language - Current language
 */
export const formatRelativeTime = (date, language = 'en') => {
  const now = new Date();
  const then = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return t('now', language);
  } else if (diffMins < 60) {
    return `${diffMins}${t('mins', language)} ${t('ago', language)}`;
  } else if (diffHours < 24) {
    return `${diffHours}${t('hrs', language)} ${t('ago', language)}`;
  } else if (diffDays < 7) {
    return `${diffDays}${t('daysShort', language)} ${t('ago', language)}`;
  } else {
    return formatDate(then, language);
  }
};

/**
 * Enhanced RTL application with proper class management
 * @param {string} language - Current language
 */
export const applyRTL = (language) => {
  const htmlElement = document.documentElement;
  const isRtl = isRTL(language);
  
  // Set direction attribute
  htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  htmlElement.setAttribute('lang', language);
  
  // Manage RTL class properly
  if (isRtl) {
    htmlElement.classList.add('rtl-layout');
  } else {
    htmlElement.classList.remove('rtl-layout');
  }
  
  // Force a repaint to ensure styles are applied
  htmlElement.style.display = 'none';
  // Trigger reflow
  void htmlElement.offsetHeight;
  htmlElement.style.display = '';
  
  console.log(`Applied ${isRtl ? 'RTL' : 'LTR'} layout for language: ${language}`);
};

/**
 * Get text direction for inline styles
 * @param {string} language - Current language
 */
export const getTextDirection = (language) => {
  return isRTL(language) ? 'rtl' : 'ltr';
};

/**
 * Enhanced translation hook for React components
 * @param {string} language - Current language
 */
export const useTranslation = (language) => {
  const translate = (key, params = {}, fallback = null) => {
    return t(key, language, params, fallback);
  };

  const translatePlural = (key, count) => {
    return tPlural(key, count, language);
  };

  return {
    t: translate,
    tPlural: translatePlural,
    formatNumber: (num) => formatNumber(num, language),
    formatCurrency: (amount, currency) => formatCurrency(amount, currency, language),
    formatDate: (date, options) => formatDate(date, language, options),
    formatRelativeTime: (date) => formatRelativeTime(date, language),
    isRTL: isRTL(language),
    textDirection: getTextDirection(language),
    applyRTL: () => applyRTL(language)
  };
};

/**
 * Validation messages helper
 * @param {string} field - Field name
 * @param {string} type - Validation type
 * @param {string} language - Current language
 * @param {Object} params - Additional parameters
 */
export const getValidationMessage = (field, type, language = 'en', params = {}) => {
  const key = `validation.${field}.${type}`;
  return t(key, language, params, t(`validation.generic.${type}`, language, params));
};

/**
 * Tournament status translator with color coding
 * @param {string} status - Tournament status
 * @param {string} language - Current language
 */
export const translateTournamentStatus = (status, language = 'en') => {
  const statusTranslations = {
    lobby: { text: t('lobby', language), color: 'blue' },
    wait: { text: t('wait', language), color: 'yellow' },
    active: { text: t('activeStatus', language), color: 'green' },
    finish: { text: t('finish', language), color: 'purple' },
    completed: { text: t('completed', language), color: 'green' },
    cancelled: { text: t('cancelled', language), color: 'red' },
    autoCanceled: { text: t('autoCanceled', language), color: 'red' }
  };
  
  return statusTranslations[status] || { text: status, color: 'gray' };
};

/**
 * Role translator with icons
 * @param {string} role - User role
 * @param {string} language - Current language
 */
export const translateRole = (role, language = 'en') => {
  const roleTranslations = {
    main_admin: { text: t('mainAdmin', language), icon: '👑' },
    supervisor: { text: t('supervisor', language), icon: '⭐' },
    approver: { text: t('approver', language), icon: '🏅' },
    player: { text: t('player', language), icon: '🎮' }
  };
  
  return roleTranslations[role] || { text: role, icon: '👤' };
};

/**
 * Game mode display name (NOT translated - always English)
 * @param {string} mode - Game mode
 */
export const translateGameMode = (mode) => {
  // Game modes should NOT be translated - always display in English
  const modeDisplayNames = {
    'SNIPERS ONLY': 'Snipers Only',
    'RIFLES ONLY': 'Rifles Only',
    'MELEE ONLY': 'Melee Only',
    'EXECUTION ONLY': 'Execution Only',
    'EXPLOSIVES ONLY': 'Explosives Only',
    'Free for All': 'Free for All',
    'Team Match': 'Team Match',
    'Custom': 'Custom',
    'teamDeathmatch': 'Team Deathmatch',
    'domination': 'Domination',
    'searchAndDestroy': 'Search & Destroy',
    'hardpoint': 'Hardpoint',
    'killConfirmed': 'Kill Confirmed',
    'captureTheFlag': 'Capture The Flag',
    'frontline': 'Frontline',
    'freeForAll': 'Free for All',
    'gunGame': 'Gun Game',
    'sticksAndStones': 'Sticks & Stones',
    'oneShotOneKill': 'One Shot One Kill',
    'cranked': 'Cranked',
    'rapidFire': 'Rapid Fire',
    'attackOfTheUndead': 'Attack of the Undead',
    'battleRoyale': 'Battle Royale',
    'quickSolo': 'Quick Solo',
    'searchAndRescue': 'Search & Rescue'
  };

  return modeDisplayNames[mode] || mode;
};

/**
 * Format tournament display string (for modals and cards)
 * @param {Object} tournament - Tournament object
 * @param {string} language - Current language
 */
export const formatTournamentDisplay = (tournament, language = 'en') => {
  const tournamentLabel = t('tournament', language);
  const gameMode = translateGameMode(tournament.gameMode, language);
  const map = tournament.map === 'Random' ? t('random', language) : tournament.map;
  
  return `${tournamentLabel}: ${gameMode} - ${map}`;
};

/**
 * Points formatter with currency symbol
 * @param {number} points - Points amount
 * @param {string} language - Current language
 * @param {boolean} showSymbol - Whether to show currency symbol
 */
export const formatPoints = (points, language = 'en', showSymbol = true) => {
  const formattedNumber = formatNumber(points, language);
  const symbol = showSymbol ? ' ' + t('pts', language) : '';
  return formattedNumber + symbol;
};

/**
 * Error message translator for API responses
 * @param {Object} error - API error object
 * @param {string} language - Current language
 * @param {string} defaultKey - Default translation key
 */
export const translateApiError = (error, language = 'en', defaultKey = 'error') => {
  // Try to get translated error message from server
  const serverMessage = error.response?.data?.message;
  const errorCode = error.response?.data?.code;
  
  // Check if we have a specific translation for this error code
  if (errorCode) {
    const translatedError = t(`errors.${errorCode}`, language);
    if (translatedError !== `errors.${errorCode}`) {
      return translatedError;
    }
  }
  
  // Fallback to generic error message
  return serverMessage || t(defaultKey, language);
};

/**
 * Safe language change handler that preserves UI state
 * @param {string} newLanguage - New language code
 * @param {Function} setLanguage - Language state setter
 * @param {Function} onLanguageChange - Optional callback
 */
export const handleLanguageChange = (newLanguage, setLanguage, onLanguageChange = null) => {
  console.log('Changing language to:', newLanguage);
  
  // Update language state
  setLanguage(newLanguage);
  
  // Persist to localStorage
  localStorage.setItem('preferred-language', newLanguage);
  
  // Apply RTL layout
  applyRTL(newLanguage);
  
  // Call optional callback
  if (onLanguageChange) {
    onLanguageChange(newLanguage);
  }
  
  // Force a small delay to ensure proper RTL application
  setTimeout(() => {
    applyRTL(newLanguage);
  }, 100);
};

/**
 * Initialize language from localStorage and apply RTL
 * @param {string} defaultLanguage - Default language if none saved
 */
export const initializeLanguage = (defaultLanguage = 'en') => {
  const savedLanguage = localStorage.getItem('preferred-language') || defaultLanguage;
  applyRTL(savedLanguage);
  return savedLanguage;
};

/**
 * Check if current layout is RTL
 */
export const isCurrentLayoutRTL = () => {
  return document.documentElement.getAttribute('dir') === 'rtl';
};

/**
 * Get appropriate margin/padding class for RTL/LTR
 * @param {string} property - CSS property (margin, padding)
 * @param {string} side - Side (left, right, top, bottom)
 * @param {string} value - Value class
 */
export const getDirectionalClass = (property, side, value) => {
  const isRtl = isCurrentLayoutRTL();
  
  if (side === 'left' && isRtl) {
    return `${property}-right-${value}`;
  } else if (side === 'right' && isRtl) {
    return `${property}-left-${value}`;
  }
  
  return `${property}-${side}-${value}`;
};

/**
 * Level system specific translation helpers
 */

/**
 * Get level title based on level number
 * @param {number} level - Level number
 * @param {string} language - Current language
 */
export const getLevelTitle = (level, language = 'en') => {
  const levelTitles = {
    100: t('legend', language),
    90: t('sage', language),
    80: t('mythic', language),
    70: t('grandmaster', language),
    60: t('master', language),
    50: t('expert', language),
    40: t('professional', language),
    30: t('advanced', language),
    20: t('intermediate', language),
    10: t('novice', language)
  };
  
  // Find the appropriate title for the level
  for (const [minLevel, title] of Object.entries(levelTitles).sort((a, b) => b[0] - a[0])) {
    if (level >= parseInt(minLevel)) {
      return title;
    }
  }
  
  return t('beginner', language);
};

/**
 * Get badge category display name
 * @param {string} category - Badge category
 * @param {string} language - Current language
 */
export const getBadgeCategoryName = (category, language = 'en') => {
  const categoryNames = {
    'win_streak': t('winStreak', language),
    'filler': t('filler', language),
    'total_wins': t('totalWins', language),
    'points_winning': t('pointsWinning', language),
    'points_purchase': t('pointsPurchase', language),
    'multiplayer': t('multiplayer', language),
    'battle_royale': t('battleRoyale', language),
    'multiplayer_wins': t('multiplayerWins', language),
    'battle_royale_wins': t('battleRoyaleWins', language)
  };
  
  return categoryNames[category] || category;
};

/**
 * Get badge rarity display name
 * @param {string} rarity - Badge rarity
 * @param {string} language - Current language
 */
export const getBadgeRarityName = (rarity, language = 'en') => {
  const rarityNames = {
    'common': t('common', language),
    'rare': t('rare', language),
    'epic': t('epic', language),
    'legendary': t('legendary', language)
  };
  
  return rarityNames[rarity] || rarity;
};

/**
 * Format experience points with appropriate suffix
 * @param {number} exp - Experience points
 * @param {string} language - Current language
 * @param {boolean} includeLabel - Whether to include EXP label
 */
export const formatExp = (exp, language = 'en', includeLabel = false) => {
  if (!exp && exp !== 0) return '0';
  
  let formatted;
  if (exp >= 1000000) {
    formatted = `${(exp / 1000000).toFixed(1)}M`;
  } else if (exp >= 1000) {
    formatted = `${(exp / 1000).toFixed(1)}K`;
  } else {
    formatted = exp.toString();
  }
  
  return includeLabel ? `${formatted} ${t('exp', language)}` : formatted;
};

/**
 * Get badge progress tip based on category
 * @param {string} category - Badge category
 * @param {number} remaining - Remaining progress
 * @param {string} language - Current language
 */
export const getBadgeProgressTip = (category, remaining, language = 'en') => {
  const tipKey = `${category}Tip`;
  const params = { remaining };
  
  switch (category) {
    case 'win_streak':
      return t('winStreakTip', language, params, `Keep winning games consecutively to reach this milestone! You need ${remaining} more wins in a row.`);
    case 'total_wins':
      return t('totalWinsTip', language, params, `Win more tournaments to unlock this badge. ${remaining} victories to go!`);
    case 'points_winning':
      return t('pointsWinningTip', language, params, `Earn more points from winning tournaments. You need ${remaining} more points.`);
    case 'points_purchase':
      return t('pointsPurchaseTip', language, params, `Purchase more points to unlock this badge. ${remaining} points left to buy.`);
    case 'filler':
      return t('fillerTip', language, params, `Add filler players to tournaments and hope they win! ${remaining} more filler wins needed.`);
    case 'multiplayer':
      return t('multiplayerTip', language, params, `Play more multiplayer tournaments. ${remaining} games remaining.`);
    case 'battle_royale':
      return t('battleRoyaleTip', language, params, `Participate in more Battle Royale matches. ${remaining} games to go.`);
    case 'multiplayer_wins':
      return t('multiplayerWinsTip', language, params, `Win more multiplayer tournaments. ${remaining} victories needed.`);
    case 'battle_royale_wins':
      return t('battleRoyaleWinsTip', language, params, `Win more Battle Royale matches. ${remaining} wins to unlock.`);
    default:
      return t('genericTip', language, params, `Keep playing to unlock this badge! ${remaining} more to go.`);
  }
};

/**
 * Format level display tooltip
 * @param {Object} levelData - Level data object
 * @param {string} language - Current language
 */
export const formatLevelTooltip = (levelData, language = 'en') => {
  const { level, title, totalExp, expNeededForNext, isMaxLevel } = levelData;
  
  const lines = [
    `${t('level', language)} ${level} - ${title}`,
    `${formatExp(totalExp)} ${t('totalExp', language)}`,
    isMaxLevel ? 
      t('maxLevel', language) : 
      `${formatExp(expNeededForNext)} ${t('expToNext', language)}`
  ];
  
  return lines.join('\n');
};

/**
 * Format badge progress percentage
 * @param {number} percentage - Progress percentage
 * @param {string} language - Current language
 */
export const formatBadgeProgress = (percentage, language = 'en') => {
  return `${Math.round(percentage || 0)}%`;
};

/**
 * Get level icon based on level number
 * @param {number} level - Level number
 */
export const getLevelIcon = (level) => {
  if (level >= 90) return '⭐'; // Star
  if (level >= 50) return '🏆'; // Trophy
  return '⚡'; // Zap
};

/**
 * Get badge rarity color
 * @param {string} rarity - Badge rarity
 */
export const getBadgeRarityColor = (rarity) => {
  switch (rarity) {
    case 'common': return '#6b7280';
    case 'rare': return '#3b82f6';
    case 'epic': return '#8b5cf6';
    case 'legendary': return '#f59e0b';
    default: return '#6b7280';
  }
};

/**
 * Format level progress display
 * @param {Object} progressData - Progress data
 * @param {string} language - Current language
 */
export const formatLevelProgress = (progressData, language = 'en') => {
  const { currentLevelExp, expForNextLevel, progressPercentage, isMaxLevel } = progressData;
  
  const expText = `${formatExp(currentLevelExp)} / ${formatExp(expForNextLevel)} EXP`;
  const progressText = isMaxLevel ? 
    t('maxLevel', language) : 
    `${Math.round(progressPercentage)}%`;
    
  return { expText, progressText };
};

/**
 * Format completion percentage
 * @param {number} earned - Number earned
 * @param {number} total - Total available
 * @param {string} language - Current language
 */
export const formatCompletionPercentage = (earned, total, language = 'en') => {
  const percentage = total > 0 ? Math.round((earned / total) * 100) : 0;
  return `${percentage}% ${t('complete', language)}`;
};

/**
 * Format badge count display
 * @param {number} earned - Earned count
 * @param {number} total - Total count
 * @param {string} language - Current language
 */
export const formatBadgeCount = (earned, total, language = 'en') => {
  return `${formatNumber(earned, language)}/${formatNumber(total, language)}`;
};

/**
 * Format milestone display
 * @param {Object} milestone - Milestone object
 * @param {string} language - Current language
 */
export const formatMilestone = (milestone, language = 'en') => {
  const { level, title, expNeeded } = milestone;
  const levelText = `${t('level', language)} ${level}: ${title || getLevelTitle(level, language)}`;
  const expText = `${formatExp(expNeeded)} EXP`;
  
  return { levelText, expText };
};

/**
 * Admin-specific translation helpers
 */

/**
 * Format admin notification messages
 * @param {string} type - Notification type
 * @param {Object} data - Notification data
 * @param {string} language - Current language
 */
export const formatAdminNotification = (type, data, language = 'en') => {
  switch (type) {
    case 'roleChanged':
      return t('roleChangedSuccessfully', language, { 
        username: data.username, 
        role: t(data.role, language) 
      });
    case 'pointsAdded':
      return t('pointsAddedSuccessfully', language, { 
        points: formatNumber(data.points, language), 
        username: data.username 
      });
    case 'pointsRemoved':
      return t('pointsRemovedSuccessfully', language, { 
        points: formatNumber(data.points, language), 
        username: data.username 
      });
    case 'pointsSet':
      return t('pointsSetSuccessfully', language, { 
        points: formatNumber(data.points, language), 
        username: data.username 
      });
    case 'userBanned':
      return t('userBannedSuccessfully', language, { username: data.username });
    case 'userUnbanned':
      return t('userUnbannedSuccessfully', language, { username: data.username });
    case 'approverAdded':
      return t('approverAddedSuccessfully', language);
    case 'approverRemoved':
      return t('approverRemovedSuccessfully', language, { username: data.username });
    case 'matchApproved':
      return t('matchApprovedSuccessfully', language);
    case 'matchCompleted':
      return t('matchCompletedSuccessfully', language);
    case 'tournamentCancelled':
      return t('tournamentCancelledSuccessfully', language, { refundInfo: data.refundInfo || '' });
    case 'matchIdUpdated':
      return t('matchIdUpdatedSuccessfully', language);
    case 'commissionRatesUpdated':
      return t('commissionRatesUpdated', language);
    case 'withdrawalProcessed':
      return t('withdrawalProcessedSuccessfully', language, { status: data.status });
    case 'fillerAdded':
      return t('fillerAddedSuccessfully', language, { name: data.name });
    case 'fillerRemoved':
      return data.refundAmount > 0 
        ? t('fillerRemovedWithRefund', language, { amount: data.refundAmount })
        : t('fillerRemoved', language);
    case 'tournamentCreated':
      return t('tournamentCreated', language);
    case 'tournamentJoined':
      return t('joinedTournament', language);
    case 'tournamentLeft':
      return t('leftTournament', language);
    default:
      return data.message || t('success', language);
  }
};

/**
 * Format admin error messages
 * @param {string} type - Error type
 * @param {Object} error - Error object
 * @param {string} language - Current language
 */
export const formatAdminError = (type, error, language = 'en') => {
  const serverMessage = error.response?.data?.message;
  
  const errorMap = {
    'loadAdminData': t('failedToLoadAdminData', language),
    'changeUserRole': t('failedToChangeUserRole', language),
    'managePoints': t('failedToManagePoints', language),
    'updateMatchId': t('failedToUpdateMatchId', language),
    'cancelTournament': t('failedToCancelTournament', language),
    'updateCommissionRates': t('failedToUpdateCommissionRates', language),
    'processWithdrawal': t('failedToProcessWithdrawal', language),
    'searchUsers': t('failedToSearchUsers', language),
    'approveMatch': t('failedToApproveMatch', language),
    'completeMatch': t('failedToCompleteMatch', language),
    'addFiller': t('failedToAddFiller', language),
    'removeFiller': t('failedToRemoveFiller', language),
    'loadTournaments': t('failedToLoadTournaments', language),
    'loadMatchHistory': t('failedToLoadMatchHistory', language),
    'loadInbox': t('failedToLoadInbox', language),
    'loadCase': t('failedToLoadCase', language),
    'submitReport': t('failedToSubmitReport', language),
    'sendMessage': t('failedToSendMessage', language),
    'updateCaseStatus': t('failedToUpdateCaseStatus', language),
    'assignCase': t('failedToAssignCase', language),
    'deleteCase': t('failedToDeleteCase', language),
    'createCase': t('failedToCreateCase', language)
  };
  
  return serverMessage || errorMap[type] || t('error', language);
};

/**
 * Format admin validation messages
 * @param {string} type - Validation type
 * @param {Object} data - Validation data
 * @param {string} language - Current language
 */
export const formatAdminValidation = (type, data, language = 'en') => {
  const validationMap = {
    'invalidUserOrRole': t('invalidUserOrRole', language),
    'userAlreadyHasRole': t('userAlreadyHasRole', language),
    'pleaseSelectUser': t('pleaseSelectUser', language),
    'pleaseEnterValidAmount': t('pleaseEnterValidAmount', language),
    'pleaseEnterMatchId': t('pleaseEnterMatchId', language),
    'pleaseSelectWinner': t('pleaseSelectWinner', language),
    'matchIdCannotBeEmpty': t('matchIdCannotBeEmpty', language),
    'matchIdTooLong': t('matchIdTooLong', language),
    'sameAsCurrentMatchId': t('sameAsCurrentMatchId', language),
    'validNewMatchId': t('validNewMatchId', language),
    'fillerNameRequired': t('fillerNameRequired', language),
    'fillerNameTooLong': t('fillerNameTooLong', language),
    'insufficientPointsForFiller': t('insufficientPointsForFiller', language),
    'needMoreRealPlayers': t('needMoreRealPlayers', language, data),
    'pleaseFillAllFields': t('pleaseFillAllFields', language),
    'pleaseFillBothFields': t('pleaseFillBothFields', language),
    'subjectMinLength': t('subjectMinLength', language),
    'descriptionMinLength': t('descriptionMinLength', language),
    'pleaseEnterMessage': t('pleaseEnterMessage', language),
    'messageTooLong': t('messageTooLong', language)
  };
  
  return validationMap[type] || t('fieldRequired', language);
};

/**
 * Format tournament time displays for admin panel
 * @param {Date|string} date - Date to format
 * @param {string} language - Current language
 */
export const formatAdminTime = (date, language = 'en') => {
  const now = new Date();
  const then = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return t('now', language);
  } else if (diffMins < 60) {
    return `${diffMins}${t('mins', language)} ${t('ago', language)}`;
  } else if (diffHours < 24) {
    return `${diffHours}${t('hrs', language)} ${t('ago', language)}`;
  } else {
    return `${diffDays}${t('daysShort', language)} ${t('ago', language)}`;
  }
};

/**
 * Get role display with proper styling classes
 * @param {string} role - User role
 * @param {string} language - Current language
 */
export const getRoleDisplay = (role, language = 'en') => {
  const roleInfo = {
    'main_admin': {
      text: t('mainAdmin', language),
      className: 'main-admin',
      color: 'red'
    },
    'supervisor': {
      text: t('supervisor', language),
      className: 'supervisor',
      color: 'purple'
    },
    'approver': {
      text: t('approver', language),
      className: 'approver',
      color: 'blue'
    },
    'player': {
      text: t('player', language),
      className: 'player',
      color: 'green'
    }
  };
  
  return roleInfo[role] || roleInfo['player'];
};

/**
 * Format search results count
 * @param {number} count - Number of results
 * @param {string} language - Current language
 */
export const formatSearchResults = (count, language = 'en') => {
  return t('searchResults', language, { count: formatNumber(count, language) });
};

/**
 * Format pagination info
 * @param {number} current - Current page
 * @param {number} total - Total pages
 * @param {number} totalItems - Total items
 * @param {string} language - Current language
 */
export const formatPaginationInfo = (current, total, totalItems, language = 'en') => {
  return t('pageInfo', language, {
    current: formatNumber(current, language),
    total: formatNumber(total, language),
    totalItems: formatNumber(totalItems, language)
  });
};

/**
 * Format tournament participant count
 * @param {number} current - Current participants
 * @param {number} max - Maximum participants
 * @param {string} language - Current language
 */
export const formatParticipantCount = (current, max, language = 'en') => {
  return t('participantsCount', language, {
    current: formatNumber(current, language),
    max: formatNumber(max, language)
  });
};

/**
 * Format clean admin name by removing suffixes
 * @param {string} name - Original name
 */
export const cleanAdminName = (name) => {
  if (!name) return name;
  
  // List of suffixes to remove (case insensitive)
  const suffixes = [
    'ADMIN', 'APPROVER', 'SUPERVISOR',
    'admin', 'approver', 'supervisor',
    '_admin', '_approver', '_supervisor',
    '[admin]', '[approver]', '[supervisor]',
    '(admin)', '(approver)', '(supervisor)'
  ];
  
  let cleaned = name;
  
  // Remove each suffix if found at the end
  for (const suffix of suffixes) {
    if (cleaned.toLowerCase().endsWith(suffix.toLowerCase())) {
      cleaned = cleaned.substring(0, cleaned.length - suffix.length);
      break; // Only remove one suffix
    }
  }
  
  return cleaned.trim();
};

/**
 * Get tournament age and cleanup warnings
 * @param {string} createdAt - Tournament creation date
 * @param {string} language - Current language
 */
export const getTournamentAgeInfo = (createdAt, language = 'en') => {
  const now = new Date();
  const created = new Date(createdAt);
  const diffMs = now - created;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins >= 25) { // 25+ minutes - critical warning
    return {
      show: true,
      text: t('warningAutoCancel', language, { time: 30 - diffMins }),
      critical: true
    };
  } else if (diffMins >= 20) { // 20+ minutes - warning
    return {
      show: true,
      text: t('warningTimeRemaining', language, { time: 30 - diffMins }),
      critical: false
    };
  }
  
  return { show: false };
};

/**
 * Format copy functionality messages
 * @param {string} type - Copy type (matchId, tournamentId)
 * @param {string|null} id - ID to copy
 * @param {string} language - Current language
 */
export const formatCopyMessage = (type, id, language = 'en') => {
  if (!id) {
    return t(`no${type.charAt(0).toUpperCase() + type.slice(1)}ToCopy`, language);
  }
  
  // Copy the ID to clipboard
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(id);
  }
  
  return t(`${type}Copied`, language);
};

export default {
  // Core translation functions
  t,
  tPlural,
  formatNumber,
  formatCurrency,
  formatDate,
  formatRelativeTime,
  applyRTL,
  getTextDirection,
  useTranslation,
  
  // General helper functions
  getValidationMessage,
  translateTournamentStatus,
  translateRole,
  translateGameMode,
  formatTournamentDisplay,
  formatPoints,
  translateApiError,
  handleLanguageChange,
  initializeLanguage,
  isCurrentLayoutRTL,
  getDirectionalClass,
  
  // Level system specific functions
  getLevelTitle,
  getBadgeCategoryName,
  getBadgeRarityName,
  formatExp,
  getBadgeProgressTip,
  formatLevelTooltip,
  formatBadgeProgress,
  getLevelIcon,
  getBadgeRarityColor,
  formatLevelProgress,
  formatCompletionPercentage,
  formatBadgeCount,
  formatMilestone,
  
  // Admin-specific functions
  formatAdminNotification,
  formatAdminError,
  formatAdminValidation,
  formatAdminTime,
  getRoleDisplay,
  formatSearchResults,
  formatPaginationInfo,
  formatParticipantCount,
  cleanAdminName,
  getTournamentAgeInfo,
  formatCopyMessage
};